"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const bluebird_1 = __importDefault(require("bluebird"));
const businessCalculator_1 = __importDefault(require("@powerednow/shared/constants/businessCalculator"));
const complexData_1 = __importDefault(require("@powerednow/complexData/complexData"));
const math_1 = __importDefault(require("@powerednow/shared/modules/utilities/math"));
const entity_1 = __importDefault(require("./entity"));
const modelProperties_1 = __importDefault(require("./modelProperties"));
const modelLookup_1 = require("../modelLookup");
const renderData_1 = __importDefault(require("./renderData"));
// eslint-disable-next-line no-redeclare
class JobItemGroup extends complexData_1.default {
    getDocumentItemType() { return 'JobItem'; }
    static get allowedAssociations() {
        return {
            job: {
                key: 'job',
                instance: (0, modelLookup_1.complexFindModel)('Job'),
                single: true,
                condition: {
                    id: this.Entity.getForeignFieldSymbols().job_id,
                },
            },
            jobItem: {
                key: 'jobItem',
                instance: (0, modelLookup_1.complexFindModel)('JobItem'),
                cascadeDelete: true,
                condition: {
                    jobitemgroup_id: this.Entity.getFieldSymbols().id,
                },
            },
            documentItem: {
                key: 'documentItem',
                instance: (0, modelLookup_1.complexFindModel)('DocumentItem'),
                single: true,
                cascadeDelete: true,
                condition: {
                    type: 'JobItem',
                    linked_id: this.Entity.getFieldSymbols().id,
                },
            },
            userTaxRate: {
                key: 'userTaxRate',
                instance: (0, modelLookup_1.complexFindModel)('UserTaxRate'),
                single: true,
                cascadeDelete: false,
                condition: {
                    id: this.Entity.getForeignFieldSymbols().vat_id,
                },
            },
            company: {
                key: 'company',
                instance: (0, modelLookup_1.complexFindModel)('Company'),
                single: true,
                condition: {
                    id: this.Entity.getFieldSymbols().company_id,
                },
            },
        };
    }
    static get indexedSearchConfig() {
        return {
            jobItem: true,
        };
    }
    async isAllowed(documentGroupType, targetCountryId) {
        const jobItems = await this.getAllJobItem();
        const sourceCountryId = await this.findCompanyCountryId();
        const jobItemResults = await bluebird_1.default.map(jobItems, async (jobItem) => jobItem.isAllowed({
            sourceCountryId,
            targetCountryId,
            documentGroupType,
        }));
        return jobItemResults.every(jobItemIsAllowed => jobItemIsAllowed === true);
    }
    async findCustomerCountryId() {
        const customer = await this.findCustomer();
        return customer.findCountryId();
    }
    async findCompanyCountryId() {
        const customer = await this.findCustomer();
        const company = await customer.getCompany();
        return company.findCountryId();
    }
    async findCustomer() {
        const job = await this.getJob();
        return job.getCustomer();
    }
    /**
     * Execute all calculations and update data
     */
    async calculate(options = {}) {
        //
        // Recalculate all lines
        //
        await this.recalculateAllLines(options);
        //
        // Update discount
        //
        this.calculateDiscount();
        //
        // Update tax by discount
        //
        this.calculateDiscountTax();
        //
        // Calculate CIS
        //
        this.calculateCIS();
        //
        // Update the final total
        //
        this.updateTotals();
        this.emit(businessCalculator_1.default.EVENTS.GROUP_RECALCULATED, this.getData());
        return this.getData();
    }
    /**
     * Iterate on all lines, recalculate them and update the group totals.
     * NOTE: this function empties the calculated fields before calculation is started
     */
    async recalculateAllLines(options = {}) {
        this.resetCalculatedFields();
        (await this.getAllJobItem()).forEach(line => {
            this.recalculateLine(line, options);
        });
    }
    recalculateLine(jobItem, options = {}) {
        const newData = jobItem.calculate({
            cloneData: false,
            cisEnabled: jobItem.data.is_cis,
            use_tax: this.data.vat_registered,
            is_inclusive: this.data.tax_inclusive,
            priceMode: options.priceMode,
        });
        this.updateGroupTotals(newData, options);
        return newData;
    }
    updateGroupTotals(lineData, options = {}) {
        //
        // Subtotal
        //
        if (!this.data.vat_registered || this.data.tax_inclusive) {
            this.data.subtotal += lineData.cost;
        }
        else {
            this.data.subtotal += lineData.exclusive_cost;
        }
        //
        // VAT
        //
        this.data.vat += lineData.vat;
        //
        // CIS - only if enabled and only if not in tax inclusive mode
        //
        if (options.cisEnabled
            && !(this.data.vat_registered
                && this.data.tax_inclusive)
            && lineData.category_is_cis) {
            this.data.cis_estimate += lineData.cis_value;
            if (!math_1.default.nearlyEqual(lineData.cis_value, 0, 0.00001)) {
                this.data.cisable_amount += lineData.exclusive_cost;
            }
        }
    }
    /**
     * Recalculate CIS if there is any discount. In that case CIS amount and
     * the CISable amount is pro-rated by the discount value
     */
    calculateCIS() {
        //
        // Pro-rate CIS value
        //
        const originalCIS = this.data.cis_estimate;
        if (this.data.cis_estimate > 0 && this.data.discount !== 0) {
            this.data.cis_estimate = this.data.subtotal === 0
                ? 0 : this.data.cis_estimate + ((this.data.discount / this.data.subtotal) * this.data.cis_estimate);
        }
        //
        // Pro-rate CISable amount
        //
        if (this.data.cis_estimate > 0 && this.data.cisable_amount > 0) {
            this.data.cisable_amount = (this.data.cisable_amount / originalCIS) * this.data.cis_estimate;
        }
    }
    /**
     * Calculate the discount amount
     *
     * @returns {number}
     */
    calculateDiscount() {
        let discount = 0;
        if (this.data.discount_type === 0) {
            discount = (this.data.discount_amount * this.data.subtotal) / 100;
        }
        else {
            discount = this.data.discount_amount || 0;
        }
        if (this.data.is_surcharge === 0) {
            discount *= -1;
        }
        this.data.discount = discount;
        return discount;
    }
    /**
     * Calculate VAT on the discount
     *
     * @returns {number}
     * @private
     */
    calculateDiscountTax() {
        let discountTax = 0;
        if (this.data.vat_registered) {
            if (this.data.tax_inclusive) {
                const groupTaxRate = math_1.default.calculateProRate(this.data.subtotal - this.data.vat, this.data.vat);
                discountTax = math_1.default.calculateIncludedTax(this.data.discount, groupTaxRate);
            }
            else {
                discountTax = this.data.subtotal === 0
                    ? 0 : this.data.vat * (this.data.discount / this.data.subtotal);
            }
        }
        // this.data.vatTotal = this.data.vat;
        this.data.vat += discountTax;
        return discountTax;
    }
    updateTotals() {
        if (!this.data.vat_registered
            || (this.data.vat_registered && this.data.tax_inclusive)) {
            this.data.total = this.data.subtotal + this.data.discount;
        }
        else {
            this.data.total = this.data.vat + this.data.subtotal + this.data.discount;
        }
    }
    resetCalculatedFields() {
        this.data.total = 0;
        this.data.discount = 0;
        this.data.subtotal = 0;
        this.data.vat = 0;
        this.data.cis_estimate = 0;
        this.data.cisable_amount = 0;
    }
    async getDocument() {
        const documentItem = await this.getDocumentItem();
        return documentItem.getDocument();
    }
    async getDocumentRenderData(L) {
        const renderer = new renderData_1.default(L, this);
        return renderer.getDocumentTemplateData();
    }
    async refreshLayoutSettings() {
        const company = await this.getCompany();
        [
            'priced_item_invoice_layout',
            'priced_item_quote_layout',
            'priced_item_worksheet_layout',
            'priced_item_creditnote_layout',
            'priced_item_expense_layout',
            'priced_item_expense_creditnote_layout',
            'priced_item_purchase_order_layout',
            'priced_item_supplier_invoice_layout',
            'priced_item_supplier_invoice_credit_layout',
        ].forEach(key => {
            this.data[key] = company.data[key];
        });
    }
    /**
     * Checks whether the current company settings are matching the settings
     * stored in the record. I.e. this record was created with the actual tax settings
     *
     * @param isExpense {boolean}
     * @returns {boolean}
     */
    async isCurrentTaxSettingsUsed(isExpense = false) {
        const compData = (await this.getCompany()).data;
        const taxInclusive = !compData.vat_registered
            || (!isExpense && compData.tax_inclusive)
            || (isExpense && compData.tax_inclusive_expense);
        return ((this.data.vat_registered === compData.vat_registered)
            && (!compData.vat_registered || this.data.tax_inclusive === taxInclusive)
            && await this.linesUsingSameTaxSetting());
    }
    async linesUsingSameTaxSetting() {
        const jobItems = await this.getAllJobItem();
        return jobItems.reduce((previousValue, currentValue) => previousValue
            && Boolean(currentValue.data.use_tax) === Boolean(this.data.vat_registered)
            && Boolean(currentValue.data.is_inclusive) === Boolean(this.data.tax_inclusive), true);
    }
    async setCurrentTaxSettings() {
        const companyData = (await this.getCompany()).data;
        this.data.vat_registered = companyData.vat_registered;
        this.data.tax_inclusive = companyData.tax_inclusive;
    }
    async saveCurrentTaxSettingsRecursively() {
        await this.setCurrentTaxSettings();
        const items = await this.getAllJobItem();
        await bluebird_1.default.each(items, async (item) => item.setCurrentTaxSettings());
        await this.calculate();
    }
}
JobItemGroup.Entity = entity_1.default;
JobItemGroup.modelProperties = modelProperties_1.default;
exports.default = JobItemGroup;
//# sourceMappingURL=../../../../src/models/jobItemGroup/index.js.map